/**
 * Matriz papel → permissões.
 *
 * Tradução literal de `docs/spec/04-roles-permissions.md §3`. Onde a spec marca
 * `P` (permitido com restrição de escopo), aqui é `true`: o recorte de registros
 * é responsabilidade do filtro de escopo (pergunta 2) e da checagem de
 * propriedade (pergunta 3), não da matriz.
 *
 * Cada linha comentada aponta a célula da spec que a justifica. Alterar uma
 * célula aqui é alterar a política de segurança do sistema — passa por revisão.
 */
import type { Papel } from './papeis'
import { PERMISSOES, type Permissao } from './permissoes'

const ATENDENTE: ReadonlyArray<Permissao> = [
  // §3.1 — conta própria
  'CONTA_ALTERAR_SENHA',
  'CONTA_EDITAR_PERFIL',
  'CONTA_ENCERRAR_SESSOES',

  // §3.2 — trabalha os leads sob sua responsabilidade
  'LEAD_LER',
  'LEAD_CRIAR', // nota ¹: só para equipe da qual é membro, responsável = ele
  'LEAD_EDITAR',
  // 'LEAD_ASSUMIR' saiu: assumir só se aplica a lead sem responsável, e o escopo do
  // ATENDENTE deixou de alcançar esses leads (C14, ver `escopo.ts`). A atribuição é
  // ato de quem distribui — SUPERVISOR para cima.
  'LEAD_TRANSFERIR', // nota ²: só lead próprio, e só para equipe da qual é membro
  'LEAD_EXPORTAR', // nota ³: só os próprios, sempre auditado
  'INTERACAO_CRIAR',
  'INTERACAO_LER_PAYLOAD_INTEGRACAO', // RN-64 (⚠️ CONFIRMAR — ver docs/security/03)

  'CLIENTE_LER',
  'CLIENTE_EDITAR',

  // §3.3 — calendário próprio
  'AGENDAMENTO_LER',
  'AGENDAMENTO_CRIAR',
  'AGENDAMENTO_GERENCIAR', // concluir/cancelar/reagendar os próprios

  // §3.4 — propostas dos próprios leads (Fase 2)
  'PROPOSTA_LER',
  'PROPOSTA_CRIAR',
  'PROPOSTA_EDITAR',

  // §3.6 — "ler qualquer catálogo (para uso em selects)"
  'CATALOGO_LER',

  // §3.7 — indicadores do próprio escopo
  'DASHBOARD_LER',
  'PAINEL_LER',
  'AVALIACAO_LER', // nota ⁸: só as do próprio atendimento
]

const SUPERVISOR: ReadonlyArray<Permissao> = [
  ...ATENDENTE,

  // §3.2 — vê e redistribui os leads das suas equipes
  /**
   * Declarado aqui, e não herdado do ATENDENTE, desde que `LEAD_ASSUMIR` saiu de lá.
   * É o primeiro papel cujo escopo alcança lead com `responsavel_id IS NULL` (equipe
   * inteira), então é o primeiro que consegue assumir um. GESTOR e ADMINISTRADOR
   * herdam desta linha.
   */
  'LEAD_ASSUMIR',
  'LEAD_TRANSFERIR_LOTE',

  'CLIENTE_HISTORICO_INTEGRAL', // §2, RN-17

  // §3.3 — calendário das suas equipes
  'AGENDAMENTO_LER_DE_OUTROS',
  'AGENDAMENTO_CRIAR_PARA_OUTRO',

  // §3.5 — leitura da equipe + "Receber Leads"
  'USUARIO_LER',
  'USUARIO_ALTERNAR_RECEBER_LEADS',
  'USUARIO_TRANSFERIR_LEADS',

  // §3.7 — produtividade do time
  'RELATORIO_VENDAS_LER',
  'RELATORIO_PROPOSTAS_LER',
  'RELATORIO_EXPORTAR',
]

const GESTOR: ReadonlyArray<Permissao> = [
  ...SUPERVISOR,

  // §3.2 — reabre lead terminal e importa
  'LEAD_REABRIR',
  'LEAD_IMPORTAR',

  // §3.5 — gerencia ATENDENTE/SUPERVISOR da própria unidade (notas ⁴ e ⁵).
  // NÃO recebe USUARIO_GERENCIAR_ADMINISTRADOR nem USUARIO_REDEFINIR_SENHA
  // (nota ⁶) nem USUARIO_VINCULAR_UNIDADES.
  'USUARIO_CRIAR',
  'USUARIO_EDITAR',
  'USUARIO_ALTERAR_PAPEL',
  'USUARIO_ATIVAR_DESATIVAR',
  'USUARIO_ENVIAR_CONVITE',
  'USUARIO_VINCULAR_EQUIPES',

  // §3.6 — equipes da unidade, motivos e tags (nota ⁷)
  'EQUIPE_GERENCIAR',
  'CATALOGO_GERENCIAR_OPERACIONAL',

  // §3.8 — auditoria da própria unidade
  'AUDITORIA_LER',

  /**
   * Meta Ads — leitura e análise, **sem** `META_ADS_GERENCIAR`.
   *
   * O gestor é quem cobra o resultado do investimento em anúncio, então precisa ver
   * o painel e pedir a análise. Mas pausar anúncio e mover orçamento é irreversível
   * do ponto de vista do dinheiro já gasto e afeta a empresa inteira, não a unidade
   * dele — fica com o Administrador (§22 da missão do agente 24).
   */
  'META_ADS_LER',
  'META_ADS_ANALISAR',
]

/** Administrador tem tudo (§3, coluna ADMIN: ✔ em toda linha aplicável). */
const ADMINISTRADOR: ReadonlyArray<Permissao> = [...PERMISSOES]

export const MATRIZ_PERMISSOES: Readonly<Record<Papel, ReadonlyArray<Permissao>>> = {
  ADMINISTRADOR,
  GESTOR,
  SUPERVISOR,
  ATENDENTE,
}

const CONJUNTOS: Readonly<Record<Papel, ReadonlySet<Permissao>>> = {
  ADMINISTRADOR: new Set(ADMINISTRADOR),
  GESTOR: new Set(GESTOR),
  SUPERVISOR: new Set(SUPERVISOR),
  ATENDENTE: new Set(ATENDENTE),
}

/** Pergunta 1 de `04-roles §4`. Não responde escopo nem propriedade. */
export function papelTemPermissao(papel: Papel, permissao: Permissao): boolean {
  return CONJUNTOS[papel].has(permissao)
}

/** Permissões efetivas do papel, sem repetição e em ordem estável. */
export function permissoesDoPapel(papel: Papel): ReadonlyArray<Permissao> {
  return PERMISSOES.filter((permissao) => CONJUNTOS[papel].has(permissao))
}
